import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  PERSONAS,
  PERSONA_INTENTS,
  PERSONA_LABELS,
  type Persona,
} from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import { Tabs } from "../components/Tabs";
import { EmptyState } from "../components/EmptyState";
import { Field, SelectField } from "../components/Field";
import { Icon } from "../components/Icon";
import "./groundtruth.css";

/**
 * PRODUCT_FLOW.md Stage 3 — Persona & Prompt Matrix.
 *
 * A configured Brand has pillars × personas × versions, which for a real
 * customer is hundreds of rows — Hindustan Zinc's seed produced 126 prompts
 * from seven pillars. So this is a searchable, sortable, paginated table rather
 * than a nested list: the operator's actual task is "show me the regulator
 * prompts for water", not "scroll".
 */

interface MatrixVersion {
  promptVersionId: string;
  text: string;
  versionNumber: number;
  approvalStatus: "pending" | "approved" | "rejected";
  rationale: string | null;
  isActive: boolean;
  createdAt: string;
}

interface MatrixTemplate {
  templateId: string;
  pillarId: string;
  persona: Persona;
  origin: "auto_generated" | "manual";
  versions: MatrixVersion[];
}

interface Matrix {
  enabledPersonas: Persona[];
  pillars: { pillarId: string; name: string }[];
  templates: MatrixTemplate[];
}

/** One flattened row per prompt version — what the table actually lists. */
interface PromptRow extends MatrixVersion {
  templateId: string;
  pillarId: string;
  pillarName: string;
  persona: Persona;
  origin: MatrixTemplate["origin"];
}

export function PromptsPage() {
  const { activeBrand, can } = useAuth();
  const brandId = activeBrand?.brandId;

  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setMatrix(await api<Matrix>(`/brands/${brandId}/prompt-matrix`));
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not load the prompt matrix.",
      );
      setMatrix({ enabledPersonas: [], pillars: [], templates: [] });
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo<PromptRow[] | null>(() => {
    if (!matrix) return null;
    const pillarName = new Map(matrix.pillars.map((p) => [p.pillarId, p.name]));
    const flat = matrix.templates.flatMap((t) =>
      t.versions.map((v) => ({
        ...v,
        templateId: t.templateId,
        pillarId: t.pillarId,
        pillarName: pillarName.get(t.pillarId) ?? "Unknown pillar",
        persona: t.persona,
        origin: t.origin,
      })),
    );
    return statusFilter === "all"
      ? flat
      : flat.filter((r) => r.approvalStatus === statusFilter);
  }, [matrix, statusFilter]);

  async function review(
    promptVersionId: string,
    approvalStatus: "approved" | "rejected" | "pending",
  ) {
    setBusyId(promptVersionId);
    setError(null);
    try {
      await api(`/prompt-versions/${promptVersionId}`, {
        method: "PATCH",
        body: { approvalStatus },
      });
      // Under a status filter the row leaves the current view the moment it
      // changes state. Without this the action looks like it silently failed,
      // which is what sent Rakesh to the refresh button.
      setNotice(
        approvalStatus === "approved"
          ? `Prompt approved${statusFilter === "pending" ? " — it has moved to the Approved tab." : "."}`
          : approvalStatus === "rejected"
            ? "Prompt rejected. It will not be run against any engine."
            : "Prompt moved back to pending.",
      );
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not update that prompt.",
      );
    } finally {
      setBusyId(null);
    }
  }

  if (!activeBrand) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">Personas &amp; Prompts</h1>
        </header>
        <div className="card">
          <EmptyState
            icon="brand"
            title="No Brand selected"
            description="Choose a Brand from the switcher to see its prompt matrix."
          />
        </div>
      </>
    );
  }

  const mayApprove = can("brand:approve_prompts");
  const pendingCount = (rows ?? []).filter(
    (r) => r.approvalStatus === "pending",
  ).length;

  // Extracted so the tab definitions stay readable — same content as before.
  const renderPromptDetail = (row: PromptRow) => (
    <div className="gt-claimdetail">
      <p className="gt-promptfull">{row.text}</p>
      {row.rationale && (
        <p className="gt-claimdetail__original">
          Why this persona asks it: {row.rationale}
        </p>
      )}
      <p className="gt-claimdetail__original">
        {PERSONA_LABELS[row.persona]} — {PERSONA_INTENTS[row.persona]}
      </p>
      {mayApprove && (
        <div className="gt-claim__actions">
          {row.approvalStatus !== "pending" && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyId === row.promptVersionId}
              onClick={() => void review(row.promptVersionId, "pending")}
            >
              Move back to pending
            </button>
          )}
          {row.approvalStatus !== "rejected" && (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={busyId === row.promptVersionId}
              onClick={() => void review(row.promptVersionId, "rejected")}
            >
              Reject
            </button>
          )}
          {row.approvalStatus !== "approved" && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busyId === row.promptVersionId}
              onClick={() => void review(row.promptVersionId, "approved")}
            >
              <Icon name="check" size={16} />
              Approve
            </button>
          )}
        </div>
      )}
    </div>
  );

  const promptColumns: Column<PromptRow>[] = [
    {
      key: "text",
      header: "Prompt",
      value: (r) => r.text,
      render: (r) => <span className="gt-claimcell">{r.text}</span>,
    },
    {
      key: "persona",
      header: "Persona",
      width: "14%",
      value: (r) => PERSONA_LABELS[r.persona],
      render: (r) => (
        <span className="badge badge--info">{PERSONA_LABELS[r.persona]}</span>
      ),
    },
    {
      key: "pillar",
      header: "Pillar",
      width: "20%",
      value: (r) => r.pillarName,
      render: (r) => (
        <span className="badge badge--accent">{r.pillarName}</span>
      ),
    },
    {
      key: "version",
      header: "Ver.",
      align: "right",
      width: "6%",
      value: (r) => r.versionNumber,
      render: (r) => <span className="tabular">v{r.versionNumber}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "12%",
      value: (r) => r.approvalStatus,
      render: (r) => (
        <>
          <span
            className={`badge ${
              r.approvalStatus === "approved"
                ? "badge--success"
                : r.approvalStatus === "rejected"
                  ? "badge--error"
                  : "badge--warning"
            }`}
          >
            {r.approvalStatus}
          </span>
          {r.isActive && (
            <span
              className="badge badge--neutral"
              style={{ marginLeft: "var(--space-1)" }}
            >
              live
            </span>
          )}
        </>
      ),
    },
    ...(mayApprove
      ? [
          {
            key: "actions",
            header: "Review",
            width: "15%",
            render: (r: PromptRow) => (
              <div className="gt-rowactions">
                {r.approvalStatus !== "approved" ? (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={busyId === r.promptVersionId}
                    onClick={() => void review(r.promptVersionId, "approved")}
                  >
                    <Icon name="check" size={16} />
                    Approve
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busyId === r.promptVersionId}
                    onClick={() => void review(r.promptVersionId, "pending")}
                  >
                    Unapprove
                  </button>
                )}
                {r.approvalStatus !== "rejected" && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busyId === r.promptVersionId}
                    onClick={() => void review(r.promptVersionId, "rejected")}
                  >
                    Reject
                  </button>
                )}
              </div>
            ),
          } as Column<PromptRow>,
        ]
      : []),
  ];

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Personas &amp; Prompts</h1>
        <p className="page__subtitle">
          The stakeholder questions Echo will ask each engine about{" "}
          <strong>{activeBrand.brandName}</strong>. These are the exact words
          that leave our system — they never contain your Ground Truth.
        </p>
      </header>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      )}

      <Tabs
        ariaLabel="Prompt matrix sections"
        tabs={[
          {
            id: "matrix",
            label: "Prompt matrix",
            icon: "prompts",
            badge: pendingCount || null,
            render: () => (
              <section className="card gt-section" style={{ padding: 0 }}>
                <DataTable
                  rows={rows}
                  rowKey={(r) => r.promptVersionId}
                  searchPlaceholder="Search prompts, pillars, or personas…"
                  pageSize={20}
                  toolbar={
                    <div className="gt-filterrow">
                      {(
                        ["all", "pending", "approved", "rejected"] as const
                      ).map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={`btn btn--sm ${statusFilter === value ? "btn--primary" : "btn--ghost"}`}
                          onClick={() => setStatusFilter(value)}
                        >
                          {value === "all"
                            ? "All"
                            : value[0]!.toUpperCase() + value.slice(1)}
                        </button>
                      ))}
                    </div>
                  }
                  empty={{
                    icon: "prompts",
                    title: "No prompts yet",
                    description:
                      "Generate candidate prompts for a pillar and persona, or author one by hand. Only approved prompts are ever run against an engine.",
                  }}
                  renderExpanded={renderPromptDetail}
                  columns={promptColumns}
                />
              </section>
            ),
          },
          {
            id: "add",
            label: "Add prompts",
            icon: "plus",
            badge: null,
            render: () =>
              matrix && matrix.pillars.length > 0 && mayApprove ? (
                <GenerateSection
                  pillars={matrix.pillars}
                  enabledPersonas={matrix.enabledPersonas}
                  onGenerated={load}
                  onNotice={setNotice}
                  onError={setError}
                />
              ) : (
                <div className="card">
                  <EmptyState
                    icon="sparkle"
                    title="Nothing to generate from yet"
                    description="Add a Narrative Pillar with approved claims in Ground Truth first — prompts are derived from what the company officially says."
                  />
                </div>
              ),
          },
          {
            id: "personas",
            label: "Personas",
            icon: "users",
            badge: matrix?.enabledPersonas.length ?? null,
            render: () => (
              <PersonaSection
                brandId={activeBrand.brandId}
                canConfigure={can("brand:configure_engines")}
              />
            ),
          },
        ]}
      />
    </>
  );
}

/* ------------------------------- Personas ------------------------------ */

function PersonaSection({
  brandId,
  canConfigure,
}: {
  brandId: string;
  canConfigure: boolean;
}) {
  const [enabled, setEnabled] = useState<Record<string, boolean> | null>(null);

  const load = useCallback(async () => {
    const result = await api<{
      items: { persona: Persona; enabled: boolean }[];
    }>(`/brands/${brandId}/personas`);
    setEnabled(
      Object.fromEntries(result.items.map((i) => [i.persona, i.enabled])),
    );
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(persona: Persona, next: boolean) {
    setEnabled((prev) => ({ ...(prev ?? {}), [persona]: next }));
    await api(`/brands/${brandId}/personas/${persona}`, {
      method: "PATCH",
      body: { enabled: next },
    });
  }

  return (
    <section className="card gt-section">
      <div className="gt-section__head">
        <h2 className="gt-section__title">Stakeholder personas</h2>
      </div>
      <p className="gt-section__lede">
        Who Echo simulates when it queries the engines. Disable any that
        don&rsquo;t apply — a private B2B business may not care what a job
        seeker is told.
      </p>
      <ul className="gt-personas">
        {PERSONAS.map((persona) => (
          <li key={persona} className="gt-persona">
            <label className="gt-persona__label">
              <input
                type="checkbox"
                checked={enabled?.[persona] ?? true}
                disabled={!canConfigure || enabled === null}
                onChange={(e) => void toggle(persona, e.target.checked)}
              />
              <span>
                <strong>{PERSONA_LABELS[persona]}</strong>
                <span className="gt-persona__intent">
                  {PERSONA_INTENTS[persona]}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------- Generate ------------------------------ */

function GenerateSection({
  pillars,
  enabledPersonas,
  onGenerated,
  onNotice,
  onError,
}: {
  pillars: { pillarId: string; name: string }[];
  enabledPersonas: Persona[];
  onGenerated: () => Promise<void>;
  onNotice: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [pillarId, setPillarId] = useState(pillars[0]?.pillarId ?? "");
  const [persona, setPersona] = useState<Persona>(
    enabledPersonas[0] ?? "journalist",
  );
  const [manualText, setManualText] = useState("");
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    try {
      const result = await api<{
        items: unknown[];
        rejectedForLeakage: number;
      }>(`/pillars/${pillarId}/prompt-templates/generate`, {
        method: "POST",
        body: { persona },
      });
      onNotice(
        `Generated ${result.items.length} prompts.` +
          (result.rejectedForLeakage
            ? ` ${result.rejectedForLeakage} candidate${result.rejectedForLeakage === 1 ? " was" : "s were"} blocked for containing Ground Truth.`
            : ""),
      );
      await onGenerated();
    } catch (caught) {
      onError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Generation failed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function addManual(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api(`/pillars/${pillarId}/prompt-templates`, {
        method: "POST",
        body: { persona, text: manualText },
      });
      setManualText("");
      onNotice("Prompt added, awaiting approval.");
      await onGenerated();
    } catch (caught) {
      // The leakage guard surfaces here — its message explains WHY, which is
      // the teachable moment for an operator writing prompts by hand.
      onError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not add that prompt.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card gt-section">
      <div className="gt-section__head">
        <h2 className="gt-section__title">Add prompts</h2>
      </div>

      <div className="gt-uploadgrid">
        <SelectField
          label="Narrative Pillar"
          value={pillarId}
          onChange={(e) => setPillarId(e.target.value)}
        >
          {pillars.map((p) => (
            <option key={p.pillarId} value={p.pillarId}>
              {p.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Persona"
          value={persona}
          onChange={(e) => setPersona(e.target.value as Persona)}
          help={PERSONA_INTENTS[persona]}
        >
          {enabledPersonas.map((p) => (
            <option key={p} value={p}>
              {PERSONA_LABELS[p]}
            </option>
          ))}
        </SelectField>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "var(--space-6)",
        }}
      >
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !pillarId}
          onClick={() => void generate()}
        >
          <Icon name="sparkle" size={16} />
          {busy ? "Generating…" : "Generate candidates"}
        </button>
      </div>

      <form onSubmit={addManual} noValidate>
        <div className="gt-urlrow">
          <Field
            label="…or write one yourself"
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="What is this company's track record on…?"
            help="It must not contain any figure or wording from your Ground Truth — the engine has to answer from what it independently believes."
          />
          <button
            type="submit"
            className="btn btn--secondary"
            disabled={busy || manualText.length < 10}
          >
            Add
          </button>
        </div>
      </form>
    </section>
  );
}
